import { ReportHandler, Metric } from 'web-vitals';
import { analyticsService } from './AnalyticsService';
import { PerformanceMetrics, EventParameters } from '../types/analytics';

// Extended metric interface for navigation type
interface ExtendedMetric extends Metric {
  navigationType?: string;
}

// Error data interface
interface ErrorData {
  error_type: string;
  error_message: string;
  error_filename?: string;
  error_line?: number;
  error_column?: number;
  error_stack?: string | null;
  [key: string]: string | number | boolean | null | undefined;
}

/**
 * Performance Tracker Service
 * Handles Core Web Vitals collection, page load monitoring, and error tracking
 */
export class PerformanceTracker {
  private metrics: Partial<PerformanceMetrics> = {};
  private isInitialized = false;
  private errorCount = 0;
  private readonly maxErrorsToTrack = 10;

  /**
     * Initialize performance tracking
     * Sets up Core Web Vitals collection and error handlers
     */
  async initialize(): Promise<void> {
    try {
      if (this.isInitialized) {
        return;
      }

      // Initialize Core Web Vitals tracking
      await this.initializeCoreWebVitals();

      // Set up error tracking
      this.initializeErrorTracking();

      // Track page load performance
      this.trackPageLoadPerformance();

      this.isInitialized = true;
      console.log('PerformanceTracker: Initialized successfully');
    } catch (error) {
      console.error('PerformanceTracker: Initialization failed:', error);
      // Don't throw - performance tracking should not break the app
    }
  }

  /**
     * Initialize Core Web Vitals collection
     * Collects LCP, FID, CLS, FCP, and TTFB metrics
     */
  private async initializeCoreWebVitals(): Promise<void> {
    try {
      // Dynamic import to avoid blocking main bundle
      const { getCLS, getFID, getFCP, getLCP, getTTFB } = await import('web-vitals');

      const reportHandler: ReportHandler = (metric: Metric) => {
        this.handleWebVitalMetric(metric);
      };

      // Collect all Core Web Vitals
      getCLS(reportHandler);
      getFID(reportHandler);
      getFCP(reportHandler);
      getLCP(reportHandler);
      getTTFB(reportHandler);

      console.log('PerformanceTracker: Core Web Vitals collection initialized');
    } catch (error) {
      console.error('PerformanceTracker: Failed to initialize Core Web Vitals:', error);
    }
  }

  /**
     * Handle individual Web Vital metrics
     * @param {Metric} metric - Web Vital metric data
     */
  private handleWebVitalMetric(metric: Metric): void {
    try {
      // Store metric locally
      switch (metric.name) {
      case 'CLS':
        this.metrics.cumulative_layout_shift = metric.value;
        break;
      case 'FID':
        this.metrics.first_input_delay = metric.value;
        break;
      case 'FCP':
        this.metrics.first_contentful_paint = metric.value;
        break;
      case 'LCP':
        this.metrics.largest_contentful_paint = metric.value;
        break;
      case 'TTFB':
        // Map TTFB to TTI for our interface (close approximation)
        this.metrics.time_to_interactive = metric.value;
        break;
      }

      // Log individual metric to analytics
      analyticsService.logEvent('web_vital', {
        metric_name: metric.name,
        metric_value: metric.value,
        metric_rating: this.getRating(metric.name, metric.value),
        metric_delta: metric.delta,
        metric_id: metric.id,
        navigation_type: (metric as ExtendedMetric).navigationType || 'unknown',
      });

      console.log(`PerformanceTracker: ${metric.name} = ${metric.value}`);
    } catch (error) {
      console.error('PerformanceTracker: Error handling web vital metric:', error);
    }
  }

  /**
     * Get performance rating for a metric
     * @param {string} metricName - Name of the metric
     * @param {number} value - Metric value
     * @return {string} Rating (good, needs-improvement, poor)
     */
  private getRating(metricName: string, value: number): string {
    switch (metricName) {
    case 'LCP':
      return value <= 2500 ? 'good' : value <= 4000 ? 'needs-improvement' : 'poor';
    case 'FID':
      return value <= 100 ? 'good' : value <= 300 ? 'needs-improvement' : 'poor';
    case 'CLS':
      return value <= 0.1 ? 'good' : value <= 0.25 ? 'needs-improvement' : 'poor';
    case 'FCP':
      return value <= 1800 ? 'good' : value <= 3000 ? 'needs-improvement' : 'poor';
    case 'TTFB':
      return value <= 800 ? 'good' : value <= 1800 ? 'needs-improvement' : 'poor';
    default:
      return 'unknown';
    }
  }

  /**
     * Initialize error tracking
     * Sets up global error handlers for unhandled errors and promise rejections
     */
  private initializeErrorTracking(): void {
    try {
      // Handle unhandled JavaScript errors
      window.addEventListener('error', (event) => {
        this.trackError({
          error_type: 'javascript_error',
          error_message: event.message,
          error_filename: event.filename,
          error_line: event.lineno,
          error_column: event.colno,
          error_stack: event.error?.stack,
        });
      });

      // Handle unhandled promise rejections
      window.addEventListener('unhandledrejection', (event) => {
        this.trackError({
          error_type: 'unhandled_promise_rejection',
          error_message: event.reason?.message || String(event.reason),
          error_stack: event.reason?.stack,
        });
      });

      console.log('PerformanceTracker: Error tracking initialized');
    } catch (error) {
      console.error('PerformanceTracker: Failed to initialize error tracking:', error);
    }
  }

  /**
     * Track page load performance
     * Uses Navigation Timing API to collect page load metrics
     */
  private trackPageLoadPerformance(): void {
    try {
      // Wait for page to fully load
      if (document.readyState === 'complete') {
        this.collectPageLoadMetrics();
      } else {
        window.addEventListener('load', () => {
          // Small delay to ensure all metrics are available
          setTimeout(() => this.collectPageLoadMetrics(), 100);
        });
      }
    } catch (error) {
      console.error('PerformanceTracker: Failed to track page load performance:', error);
    }
  }

  /**
     * Collect page load metrics using Navigation Timing API
     */
  private collectPageLoadMetrics(): void {
    try {
      const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;

      if (!navigation) {
        console.warn('PerformanceTracker: Navigation timing not available');
        return;
      }

      const pageLoadMetrics = {
        dns_lookup_time: navigation.domainLookupEnd - navigation.domainLookupStart,
        tcp_connection_time: navigation.connectEnd - navigation.connectStart,
        server_response_time: navigation.responseStart - navigation.requestStart,
        dom_content_loaded_time: navigation.domContentLoadedEventEnd - navigation.fetchStart,
        page_load_time: navigation.loadEventEnd - navigation.fetchStart,
        redirect_time: navigation.redirectEnd - navigation.redirectStart,
        unload_time: navigation.unloadEventEnd - navigation.unloadEventStart,
      };

      // Log page load performance
      analyticsService.logEvent('page_load_performance', pageLoadMetrics);

      console.log('PerformanceTracker: Page load metrics collected', pageLoadMetrics);
    } catch (error) {
      console.error('PerformanceTracker: Failed to collect page load metrics:', error);
    }
  }

  /**
     * Track custom performance timing
     * @param {string} name - Name of the performance measurement
     * @param {number} startTime - Start time in milliseconds
     * @param {number} endTime - End time in milliseconds
     * @param {Record<string, any>} [additionalData] - Additional context data
     */
  trackCustomTiming(
    name: string,
    startTime: number,
    endTime: number,
    additionalData?: EventParameters,
  ): void {
    try {
      const duration = endTime - startTime;

      analyticsService.logEvent('custom_timing', {
        timing_name: name,
        timing_duration: duration,
        timing_start: startTime,
        timing_end: endTime,
        ...additionalData,
      });

      console.log(`PerformanceTracker: Custom timing - ${name}: ${duration}ms`);
    } catch (error) {
      console.error('PerformanceTracker: Error tracking custom timing:', error);
    }
  }

  /**
     * Track error events with context information
     * @param {ErrorData} errorData - Error information and context
     */
  trackError(errorData: ErrorData): void {
    try {
      // Limit the number of errors tracked to prevent spam
      if (this.errorCount >= this.maxErrorsToTrack) {
        return;
      }

      this.errorCount++;

      // Add context information
      const errorEvent = {
        ...errorData,
        error_timestamp: Date.now(),
        user_agent: navigator.userAgent,
        page_url: window.location.href,
        page_title: document.title,
        viewport_width: window.innerWidth,
        viewport_height: window.innerHeight,
        error_count: this.errorCount,
      };

      analyticsService.logEvent('application_error', errorEvent);

      console.error('PerformanceTracker: Error tracked', errorEvent);
    } catch (error) {
      console.error('PerformanceTracker: Failed to track error:', error);
    }
  }

  /**
     * Get current performance metrics
     * @return {Partial<PerformanceMetrics>} Current collected metrics
     */
  getMetrics(): Partial<PerformanceMetrics> {
    return { ...this.metrics };
  }

  /**
     * Check if performance tracking is initialized
     * @return {boolean} True if initialized
     */
  isReady(): boolean {
    return this.isInitialized;
  }
}

// Export singleton instance
export const performanceTracker = new PerformanceTracker();
export default performanceTracker;
